import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';


const VEIN_STATUS = [
  'Normal - Compressible, patent',
  'Acute DVT',
  'Chronic DVT',
  'Acute & Chronic DVT',
  'Partial thrombosis',
  'Not well visualized'
];

function UpperExtremityDVTForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Right Upper Extremity, 1=Left Upper Extremity
  const [activeTab, setActiveTab] = useState(0);
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();

  const INITIAL_UPPER_EXTREMITY_DVT_DATA = {
    examType: 'UpperExtremityDVT',
    examDate: new Date().toISOString().split('T')[0],
    firstName: '',
    lastName: '',
    mrn: '',
    technologist: '',
    reason: '',
    callReport: false,
    phoneNumber: '',
    history: '',
    comments: '',
    rightArmEvaluated: false,
    leftArmEvaluated: false,
    rightArm: {
      internalJugularVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      subclavianVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      axillaryVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      brachialVeins: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      basilicVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      cephalicVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      }
    },
    leftArm: {
      internalJugularVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      subclavianVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      axillaryVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      brachialVeins: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      basilicVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      cephalicVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      }
    }
  };

  const {
    formData,
    error,
    success,
    isLoading,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    updateField,
    updateNestedField,
    resetForm,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    {
      ...INITIAL_UPPER_EXTREMITY_DVT_DATA,
      technologist: defaultTech
    },
    'UpperExtremityDVT',
    {
    token, facilityInfo
  });

  useEffect(() => {
    if (defaultTech && !formData.technologist) {
      updateField('technologist', defaultTech);
    }
  }, [defaultTech, formData.technologist, updateField]);

  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    await hookHandleSubmit(e);
  }, [hookHandleSubmit]);

  const handleVeinChange = (arm, vein, field, value) => {
    // Get current arm data
    const currentArmData = formData[arm];
    // Update the specific vein's field
    const updatedArmData = {
      ...currentArmData,
      [vein]: {
        ...currentArmData[vein],
        [field]: value
      }
    };
    // Update the entire arm data
    updateField(arm, updatedArmData);
  };

  const handleArmEvaluationChange = (arm, evaluated) => {
    updateField(`${arm}Evaluated`, evaluated);
  };

  const renderVeinSection = (arm, armLabel, veinKey, veinLabel, isEvaluated) => {
    const veinData = formData[arm][veinKey];
    const isAbnormal = veinData.status !== 'Normal - Compressible, patent';

    return (
      <div className="form-group" style={{ marginBottom: '15px', opacity: isEvaluated ? 1 : 0.5 }}>
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
          {veinLabel}:
        </label>
        <select
          value={veinData.status}
          onChange={(e) => handleVeinChange(arm, veinKey, 'status', e.target.value)}
          disabled={!isEvaluated}
          style={{ 
            width: '100%', 
            marginBottom: '5px',
            backgroundColor: !isEvaluated ? 'var(--color-surface)' : (isAbnormal ? 'var(--color-status-bad-soft)' : 'var(--color-input-bg)'),
            borderColor: isAbnormal ? 'var(--color-danger)' : 'var(--color-border)',
            cursor: isEvaluated ? 'pointer' : 'not-allowed'
          }}
        >
          {VEIN_STATUS.map(status => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
        {isAbnormal && isEvaluated && (
          <input
            type="text"
            value={veinData.notes}
            onChange={(e) => handleVeinChange(arm, veinKey, 'notes', e.target.value)}
            placeholder="Additional notes..."
            style={{ width: '100%' }}
          />
        )}
      </div>
    );
  };

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={handleFormSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history (e.g., arm swelling, pain, suspected DVT)"
            />
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              className={`worksheet-tab-btn${activeTab === 0 ? ' active' : ''}`}
            >
              Right Upper Extremity
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(1)}
              className={`worksheet-tab-btn${activeTab === 1 ? ' active' : ''}`}
            >
              Left Upper Extremity
            </button>
          </div>

          {/* Tab Content */}
          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {/* Right Upper Extremity Tab */}
            {activeTab === 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, color: 'var(--color-primary)' }}>Right Upper Extremity</h3>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="rightArmEvaluation"
                  checked={formData.rightArmEvaluated}
                  onChange={() => handleArmEvaluationChange('rightArm', true)}
                />
                <span style={{ fontWeight: formData.rightArmEvaluated ? 'bold' : 'normal', color: formData.rightArmEvaluated ? 'var(--color-success)' : 'var(--color-text-secondary)' }}>
                  Evaluated
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="rightArmEvaluation"
                  checked={!formData.rightArmEvaluated}
                  onChange={() => handleArmEvaluationChange('rightArm', false)}
                />
                <span style={{ fontWeight: !formData.rightArmEvaluated ? 'bold' : 'normal', color: !formData.rightArmEvaluated ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
                  Not Evaluated
                </span>
              </label>
            </div>
          </div>
          
          {renderVeinSection('rightArm', 'Right', 'internalJugularVein', 'Internal Jugular Vein', formData.rightArmEvaluated)}
          {renderVeinSection('rightArm', 'Right', 'subclavianVein', 'Subclavian Vein', formData.rightArmEvaluated)}
          {renderVeinSection('rightArm', 'Right', 'axillaryVein', 'Axillary Vein', formData.rightArmEvaluated)}
          {renderVeinSection('rightArm', 'Right', 'brachialVeins', 'Brachial Veins', formData.rightArmEvaluated)}
          {renderVeinSection('rightArm', 'Right', 'basilicVein', 'Basilic Vein', formData.rightArmEvaluated)}
          {renderVeinSection('rightArm', 'Right', 'cephalicVein', 'Cephalic Vein', formData.rightArmEvaluated)}
              </div>
            )}

            {/* Left Upper Extremity Tab */}
            {activeTab === 1 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, color: 'var(--color-primary)' }}>Left Upper Extremity</h3>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="leftArmEvaluation"
                  checked={formData.leftArmEvaluated}
                  onChange={() => handleArmEvaluationChange('leftArm', true)}
                />
                <span style={{ fontWeight: formData.leftArmEvaluated ? 'bold' : 'normal', color: formData.leftArmEvaluated ? 'var(--color-success)' : 'var(--color-text-secondary)' }}>
                  Evaluated
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="leftArmEvaluation"
                  checked={!formData.leftArmEvaluated}
                  onChange={() => handleArmEvaluationChange('leftArm', false)}
                />
                <span style={{ fontWeight: !formData.leftArmEvaluated ? 'bold' : 'normal', color: !formData.leftArmEvaluated ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
                  Not Evaluated
                </span>
              </label>
            </div>
          </div>

          {renderVeinSection('leftArm', 'Left', 'internalJugularVein', 'Internal Jugular Vein', formData.leftArmEvaluated)}
          {renderVeinSection('leftArm', 'Left', 'subclavianVein', 'Subclavian Vein', formData.leftArmEvaluated)}
          {renderVeinSection('leftArm', 'Left', 'axillaryVein', 'Axillary Vein', formData.leftArmEvaluated)}
          {renderVeinSection('leftArm', 'Left', 'brachialVeins', 'Brachial Veins', formData.leftArmEvaluated)}
          {renderVeinSection('leftArm', 'Left', 'basilicVein', 'Basilic Vein', formData.leftArmEvaluated)}
          {renderVeinSection('leftArm', 'Left', 'cephalicVein', 'Cephalic Vein', formData.leftArmEvaluated)}
              </div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments or impressions..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button 
            type="submit" 
            disabled={isLoading} 
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button 
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default UpperExtremityDVTForm;
